import React, { useLayoutEffect, useRef } from "react";
import { ResumeDocument, ResumePage } from "./resumeLayout";
import {
  applyRenderValidation,
  validateTemplateStructure,
} from "./templateRenderPipeline.js";

export const CV_PAGE_WIDTH_MM = 210;
export const CV_PAGE_HEIGHT_MM = 297;

export const CV_GROUP_SELECTORS = [
  '[data-section$="Item"]',
  '[data-section$="item"]',
  '[data-section$="Entry"]',
  '[data-section$="entry"]',
  '[data-section$="Block"]',
  '[data-section$="block"]',
  '[data-section$="Card"]',
  '[data-section$="card"]',
  '[data-cv-group="true"]',
  ".companyBlock",
  ".eduRowBlock",
  ".timelineItem",
  ".sectionBlock",
  ".headerBox",
].join(", ");

export const CV_SECTION_SELECTORS = [
  '[data-high-style-section="true"]',
  '[data-section]',
  ".section",
  "section",
  "article",
].join(", ");

export const CV_HEADING_SELECTORS = [
  '[data-title="true"]',
  "[data-title]",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
].join(", ");

export const CV_LAYOUT_ENGINE_CSS = `
:root {
  --cv-page-width: 210mm;
  --cv-page-height: 297mm;
  --cv-space-1: 1.5mm;
  --cv-space-2: 3mm;
  --cv-space-3: 4.5mm;
  --cv-space-4: 6mm;
}

.cv-document {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  display: block;
  box-sizing: border-box;
}

.page,
.cv-page {
  width: var(--cv-page-width);
  max-width: 100%;
  min-height: auto;
  height: auto;
  margin: 0 auto;
  padding: 0;
  display: block;
  background: transparent;
  box-sizing: border-box;
  overflow: visible;
}

.cv-content {
  width: 100%;
  min-height: auto;
  height: auto;
  margin: 0;
  padding: 0;
  display: block;
  box-sizing: border-box;
  overflow: visible;
}

.cv-template-root:not([data-template-family="new-prototype"]),
.cv-template-root:not([data-template-family="new-prototype"]) > div:first-child,
.cv-template-root:not([data-template-family="new-prototype"]) > main:first-child,
.cv-template-root:not([data-template-family="new-prototype"]) > section:first-child {
  width: 100%;
  max-width: 100%;
  min-height: auto !important;
  height: auto !important;
  max-height: none !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  overflow: visible !important;
  padding: 20px;
}

.cv-template-root,
.cv-template-root *,
.cv-template-root *::before,
.cv-template-root *::after {
  box-sizing: border-box !important;
}

.cv-template-root p,
.cv-template-root li,
.cv-template-root span,
.cv-template-root div {
  word-break: normal !important;
  overflow-wrap: break-word !important;
  white-space: normal !important;
  hyphens: none !important;
}

.cv-template-root p,
.cv-template-root li {
  orphans: 2;
  widows: 2;
}

.cv-template-root [style*="text-align:justify"],
.cv-template-root [style*="text-align: justify"] {
  text-align: left !important;
  text-align-last: auto !important;
}

.cv-template-root .cv-rich-text,
.cv-template-root .cv-rich-text p,
.cv-template-root .cv-rich-text li,
.cv-template-root .cv-rich-text-inline {
  text-align: left !important;
  text-align-last: auto !important;
}

.cv-template-root .cv-rich-text ul,
.cv-template-root .cv-rich-text ol {
  margin: 0.35em 0 0;
  padding-left: 1.25em;
}

.cv-template-root .cv-rich-text li {
  margin: 0 0 0.3em;
  padding-left: 0.2em;
}

.cv-template-root .cv-rich-text li:last-child {
  margin-bottom: 0;
}

.cv-template-root img,
.cv-template-root svg,
.cv-template-root canvas {
  max-width: 100% !important;
  height: auto !important;
}

.cv-template-root [style*="display:flex"] > *,
.cv-template-root [style*="display: flex"] > *,
.cv-template-root [style*="display:grid"] > *,
.cv-template-root [style*="display: grid"] > * {
  min-width: 0 !important;
  min-height: 0 !important;
}

.cv-template-root:not([data-template-family="new-prototype"]) .cv-section {
  break-inside: auto;
  page-break-inside: auto;
  margin-block-end: var(--cv-section-gap, 8px) !important;
}

.cv-template-root:not([data-template-family="new-prototype"]) .cv-section:last-child {
  margin-block-end: 0 !important;
}

.cv-template-root [data-cv-hidden="true"] {
  display: none !important;
}

.cv-template-root .cv-section[data-cv-density="tight"] {
  --cv-section-gap: 5px;
}

.cv-template-root .cv-section[data-cv-density="balanced"] {
  --cv-section-gap: 8px;
}

.cv-template-root .cv-section[data-cv-density="relaxed"] {
  --cv-section-gap: 12px;
}

.cv-template-root .cv-section[data-cv-kind="header"] {
  --cv-section-gap: 0px;
}

.cv-template-root .cv-group {
  break-inside: auto !important;
  page-break-inside: auto !important;
}

.cv-template-root [data-title],
.cv-template-root [data-title="true"],
.cv-template-root .cv-heading,
.cv-template-root [data-field="roleTitle"] {
  word-spacing: normal;
  font-kerning: normal;
  text-rendering: geometricPrecision;
}

@media print {
  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    overflow: visible !important;
  }

  .cv-document {
    width: var(--cv-page-width) !important;
    max-width: var(--cv-page-width) !important;
    margin: 0 auto !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .page,
  .cv-page {
    width: var(--cv-page-width) !important;
    max-width: var(--cv-page-width) !important;
    min-height: auto !important;
    height: auto !important;
    margin: 0 auto !important;
    padding: 0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .cv-content {
    min-height: auto !important;
  }

  .cv-template-root,
  .cv-template-root > div:first-child,
  .cv-template-root > main:first-child,
  .cv-template-root > section:first-child,
  .cv-template-root [data-resume-root="true"],
  .cv-template-root [data-section] {
    min-height: auto !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }

  .cv-template-root .cv-group {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .cv-template-root [data-title],
  .cv-template-root [data-title="true"],
  .cv-template-root .cv-heading,
  .cv-template-root [data-field="roleTitle"] {
    letter-spacing: normal !important;
    word-spacing: normal !important;
    font-kerning: normal !important;
    text-rendering: geometricPrecision !important;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

export function normalizeCvKind(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function getKindFromNode(node) {
  if (!node) return "";

  return normalizeCvKind(
    node.getAttribute("data-cv-kind") ||
      node.getAttribute("data-high-style-kind") ||
      node.getAttribute("data-section") ||
      node.getAttribute("data-section-id"),
  );
}

function isMeaningfulNode(node) {
  if (!node) return false;
  return Boolean((node.textContent || "").trim());
}

function isHeadingNode(node) {
  if (!node?.matches) return false;
  return node.matches(CV_HEADING_SELECTORS);
}

function countDirectGroups(node) {
  return Array.from(node?.children || []).filter(
    (child) => child.classList?.contains("cv-group"),
  ).length;
}

function hasDirectSectionLikeChild(node) {
  return Array.from(node?.children || []).some((child) => {
    if (!child?.matches) return false;
    return child.matches(CV_SECTION_SELECTORS);
  });
}

function hasDirectRenderableText(node) {
  return Array.from(node?.childNodes || []).some((child) => {
    if (child.nodeType !== Node.TEXT_NODE) return false;
    return Boolean(String(child.textContent || "").trim());
  });
}

function getDescendantFieldCount(node) {
  return node?.querySelectorAll?.("[data-field]")?.length || 0;
}

function isLikelyLogicalBlock(node) {
  if (!node?.matches) return false;
  if (!isMeaningfulNode(node) || isHeadingNode(node)) return false;
  if (node.matches("[data-cv-section='true'], .cv-section")) return false;
  if (node.matches("img, svg, canvas, style, script, main, aside, header, footer")) return false;
  if (hasDirectSectionLikeChild(node)) return false;
  if ((node.querySelectorAll?.("[data-cv-section='true'], [data-high-style-section='true']").length || 0) >= 2) {
    return false;
  }

  if (node.matches("article, li")) {
    return true;
  }

  const hint = [
    node.getAttribute("data-section"),
    node.getAttribute("data-cv-kind"),
    node.className,
  ]
    .filter(Boolean)
    .join(" ");

  if (/\b(item|entry|block|card|row)\b/i.test(hint)) {
    return true;
  }

  const descendantFieldCount = getDescendantFieldCount(node);
  if (descendantFieldCount >= 2) {
    return true;
  }

  if (descendantFieldCount >= 1 && (node.textContent || "").trim().length >= 40) {
    return true;
  }

  return hasDirectRenderableText(node) && node.children.length >= 1;
}

function promoteNodeToGroup(node) {
  if (!node) return;

  node.classList.add("cv-group");
  node.setAttribute("data-cv-group", "true");
  const kind = getKindFromNode(node);
  if (kind && !node.getAttribute("data-cv-kind")) {
    node.setAttribute("data-cv-kind", kind);
  }
}

function promoteLogicalGroups(root) {
  Array.from(root.querySelectorAll(CV_GROUP_SELECTORS))
    .filter(isMeaningfulNode)
    .forEach((node) => {
      promoteNodeToGroup(node);
    });

  const containers = [root, ...Array.from(root.querySelectorAll("*"))];

  containers.forEach((container) => {
    const logicalChildren = Array.from(container.children || []).filter((child) =>
      isLikelyLogicalBlock(child),
    );

    if (logicalChildren.length === 0) return;

    if (
      logicalChildren.length === 1 &&
      !logicalChildren[0].matches(
        "article, li, [data-section$='Item'], [data-section$='item'], [data-section$='Entry'], [data-section$='entry'], [data-section$='Block'], [data-section$='block'], [data-section$='Card'], [data-section$='card']",
      )
    ) {
      return;
    }

    logicalChildren.forEach((child) => {
      promoteNodeToGroup(child);
    });
  });
}

export function applyCvLayoutSemantics(rootNode) {
  const resumeRoot =
    rootNode?.querySelector?.("[data-resume-root='true'], [data-resume-root]") ||
    rootNode ||
    null;

  if (!resumeRoot) return null;

  resumeRoot.classList.add("cv-template-root");
  resumeRoot.setAttribute("data-cv-root", "true");

  const templateFamilySource =
    resumeRoot.getAttribute("data-template-family") ||
    resumeRoot.closest?.("[data-template-family]")?.getAttribute("data-template-family") ||
    "";

  if (templateFamilySource && !resumeRoot.getAttribute("data-template-family")) {
    resumeRoot.setAttribute("data-template-family", templateFamilySource);
  }

  ["data-source-prototype", "data-prototype-layout", "data-prototype-signature"].forEach((attribute) => {
    if (resumeRoot.getAttribute(attribute)) return;
    const inheritedValue = resumeRoot.closest?.(`[${attribute}]`)?.getAttribute(attribute);
    if (inheritedValue) {
      resumeRoot.setAttribute(attribute, inheritedValue);
    }
  });

  Array.from(resumeRoot.querySelectorAll(CV_SECTION_SELECTORS))
    .filter(isMeaningfulNode)
    .forEach((node) => {
      if (isHeadingNode(node)) return;

      const kind = getKindFromNode(node);

      node.classList.add("cv-section");
      node.setAttribute("data-cv-section", "true");

      if (kind) {
        node.setAttribute("data-cv-kind", kind);
      }

      node.removeAttribute("data-cv-keep");
      node.removeAttribute("data-cv-flow");
    });

  promoteLogicalGroups(resumeRoot);

  Array.from(resumeRoot.querySelectorAll("[data-cv-section='true']")).forEach((node) => {
    const directGroupCount = countDirectGroups(node);
    if (directGroupCount > 0) {
      node.setAttribute("data-cv-flow", "items");
    } else {
      node.removeAttribute("data-cv-flow");
    }
  });

  Array.from(resumeRoot.querySelectorAll(CV_HEADING_SELECTORS))
    .filter(isMeaningfulNode)
    .forEach((node) => {
      node.classList.add("cv-heading");
      node.setAttribute("data-cv-heading", "true");
    });

  return resumeRoot;
}

export default function CvLayoutEngine({ children, templateId, resumeData }) {
  const engineRef = useRef(null);

  useLayoutEffect(() => {
    const resumeRoot = applyCvLayoutSemantics(engineRef.current);
    if (!resumeRoot) return;

    const validationResult = applyRenderValidation(resumeRoot, resumeData);

    if (
      process.env.NODE_ENV !== "production" &&
      typeof window !== "undefined" &&
      window.__CVCANVAS_TEMPLATE_DEBUG__ === true
    ) {
      const structure = validateTemplateStructure(resumeRoot, resumeData);
      if (
        structure.duplicateSections.length > 0 ||
        structure.invalidBindings.length > 0 ||
        structure.missingDataFieldContent.length > 0
      ) {
        console.warn("cvcanvas:template-structure", {
          templateId,
          duplicateSections: structure.duplicateSections,
          invalidBindings:
            structure.invalidBindings.length > 0
              ? structure.invalidBindings
              : validationResult?.invalidBindings || [],
          missingDataFieldContent: structure.missingDataFieldContent,
        });
      }
    }
  }, [resumeData, templateId]);

  return (
    <ResumeDocument data-template-id={templateId}>
      <style>{CV_LAYOUT_ENGINE_CSS}</style>
      <ResumePage>
        <div ref={engineRef} className="cv-content">
          {children}
        </div>
      </ResumePage>
    </ResumeDocument>
  );
}
